/**
 * Error types, feature detection, and error display helpers.
 *
 * Provides graceful degradation for missing browser capabilities
 * and a consistent error banner UI for user-facing messages.
 */

// ============================================================
// Error type constants
// ============================================================

export const ErrorType = {
  NO_WEBRTC: 'NO_WEBRTC',
  CAMERA_DENIED: 'CAMERA_DENIED',
  RELAY_FAILURE: 'RELAY_FAILURE',
  NO_SFRAME: 'NO_SFRAME',
  NO_INDEXEDDB: 'NO_INDEXEDDB',
  UNKNOWN: 'UNKNOWN',
} as const

export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType]

// ============================================================
// Feature detection
// ============================================================

export function hasWebRTC(): boolean {
  return (
    typeof RTCPeerConnection !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  )
}

export function hasSFrame(): boolean {
  return typeof RTCRtpScriptTransform !== 'undefined'
}

export function hasIndexedDB(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    return false
  }
}

// ============================================================
// Error banner UI
// ============================================================

let autoDismissTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Show an error banner at the top of the page.
 *
 * @param message - User-facing error text
 * @param recoverable - If true, auto-dismisses after 8 seconds.
 *                      If false, stays until user clicks to dismiss.
 */
export function showErrorBanner(message: string, recoverable = true): void {
  if (autoDismissTimer) {
    clearTimeout(autoDismissTimer)
    autoDismissTimer = null
  }

  let banner = document.getElementById('error-banner')
  if (!banner) {
    banner = document.createElement('div')
    banner.id = 'error-banner'
    banner.className = 'error-banner'
    banner.title = 'Click to dismiss'
    banner.addEventListener('click', () => {
      banner!.remove()
      if (autoDismissTimer) {
        clearTimeout(autoDismissTimer)
        autoDismissTimer = null
      }
    })
  }

  banner.textContent = message
  document.body.prepend(banner)

  if (recoverable) {
    autoDismissTimer = setTimeout(() => {
      banner?.remove()
      autoDismissTimer = null
    }, 8000)
  }
}

// ============================================================
// Camera permission error detection
// ============================================================

export function isCameraDeniedError(err: unknown): boolean {
  if (!(err instanceof DOMException)) return false
  return err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
}
